import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Heart, Flame, Eye, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";

export function viewerId() {
  let v = localStorage.getItem("hb_viewer");
  if (!v) {
    v = (crypto.randomUUID && crypto.randomUUID()) || String(Math.random()).slice(2);
    localStorage.setItem("hb_viewer", v);
  }
  return v;
}

export function MemoryCard({ memory, user, onDeleted }) {
  const [m, setM] = useState(memory);
  const ref = useRef(null);
  const seen = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || seen.current) return;
    const obs = new IntersectionObserver(async ([e]) => {
      if (e.isIntersecting && !seen.current) {
        seen.current = true;
        obs.disconnect();
        try {
          const { data } = await api.viewMemory(m.id, viewerId());
          setM((x) => ({ ...x, views: data.views }));
        } catch {}
      }
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [m.id]);

  const react = async (type) => {
    if (!user) { toast("Sign in to react"); return; }
    try {
      const { data } = await api.reactMemory(m.id, type);
      setM((x) => ({ ...x, likes: data.likes, loves: data.loves }));
    } catch {}
  };

  const remove = async () => {
    try {
      await api.deleteMemory(m.id);
      toast.success("Removed from your school page");
      onDeleted && onDeleted(m.id);
    } catch {
      toast.error("Could not remove");
    }
  };

  const mine = user && m.user_id === user.id;

  return (
    <div ref={ref} data-testid={`memory-card-${m.id}`} className="relative group overflow-hidden bg-ivory break-inside-avoid border border-border rounded-sm shadow-sm">
      <div className="relative">
        <img src={m.image_url} alt={m.caption || "Prom memory"} className="w-full object-cover object-top" />
        {m.source === "locked_look" && (
          <span className="absolute top-2.5 left-2.5 glass-dark text-ivory font-label text-[8px] px-2.5 py-1 rounded-full flex items-center gap-1"><Lock className="w-2.5 h-2.5 text-champagne" /> Locked Look</span>
        )}
        {mine && (
          <button data-testid={`memory-delete-${m.id}`} onClick={remove} title="Remove from school page" className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-ink/60 text-ivory items-center justify-center hidden group-hover:flex hover:bg-[hsl(var(--destructive))] transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="p-3">
        {m.caption && <p className="font-body text-sm leading-snug">{m.caption}</p>}
        <div className="flex items-center justify-between mt-2">
          <span className="font-label text-[8px] text-muted-foreground truncate pr-2">{m.author}</span>
          <div className="flex items-center gap-3 shrink-0">
            <button data-testid={`memory-like-${m.id}`} onClick={() => react("like")} className="flex items-center gap-1 font-body text-xs text-ink hover:text-rosemetal transition-colors"><Flame className="w-3.5 h-3.5" /> {m.likes || 0}</button>
            <button data-testid={`memory-love-${m.id}`} onClick={() => react("love")} className="flex items-center gap-1 font-body text-xs text-rosemetal"><Heart className="w-3.5 h-3.5 fill-rosemetal" /> {m.loves || 0}</button>
            <span data-testid={`memory-views-${m.id}`} className="flex items-center gap-1 font-body text-xs text-muted-foreground"><Eye className="w-3.5 h-3.5" /> {m.views || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
